/**
 * The 12 Days of Claudmas: the one place the schedule and the twelve days live.
 *
 * Read by the server (which decides what is unlocked, so a locked skill cannot
 * be fetched early by editing the page), by the member dashboard, and — over
 * the public `claudmas.days` query — by the sign-up page on yoursmartstack.com,
 * which is a separate static repository. Filling in a day here fills it in on
 * both sites; there is no second copy to forget.
 *
 * EVERY DAY BELOW IS A PLACEHOLDER. The twelve skills have not been chosen
 * yet. For each day, fill in:
 *   title        short, what the skill does ("Your brand, in one file")
 *   description  one line, the end result and why it matters, not a how-to
 *   videoId      the Open.Video id of the ~3 minute video (empty = no player)
 *   skillUrl     where the .zip download lives (empty = "skill coming")
 *
 * ── Unlocking ───────────────────────────────────────────────────────────────
 * Day 1 opens at midnight Pacific on START_DATE and one more day opens at each
 * midnight after it. Pacific is fixed at UTC-8 here: the whole run sits inside
 * standard time (DST ended Nov 1 2026), so there is no transition to get wrong.
 * If the run is ever moved into DST, change UNLOCK_UTC_OFFSET_HOURS to 7.
 *
 * ASSUMPTION to confirm: consecutive days (Nov 30 through Dec 11). If skills
 * should skip weekends, replace unlockAt() with an explicit list of dates.
 */

export const CLAUDMAS_CAMPAIGN = "claudmas-2026" as const;
export const START_DATE = "2026-11-30";
const UNLOCK_UTC_OFFSET_HOURS = 8;

export interface ClaudmasDay {
  day: number;
  title: string;
  description: string;
  videoId: string;
  skillUrl: string;
}

export const CLAUDMAS_DAYS: ClaudmasDay[] = Array.from({ length: 12 }, (_, i) => ({
  day: i + 1,
  title: `Day ${i + 1} skill (placeholder title)`,
  description: "Placeholder: one line on the result this skill gets you and why it matters.",
  videoId: "",
  skillUrl: "",
}));

/** When a day opens, as an absolute instant. */
export function unlockAt(day: number): Date {
  const [y, m, d] = START_DATE.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + (day - 1), UNLOCK_UTC_OFFSET_HOURS));
}

export function isUnlocked(day: number, now: Date = new Date()): boolean {
  return now.getTime() >= unlockAt(day).getTime();
}

/** "Mon, Nov 30", in Pacific time, for labels. */
export function unlockLabel(day: number): string {
  return unlockAt(day).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "America/Los_Angeles",
  });
}
